import { ApolloClient } from '@apollo/client';
import { Dispatch } from 'redux';

import {
  ImpactAiNonprofitDocument,
  ImpactAiNonprofitQuery,
  ImpactAiNonprofitQueryVariables,
} from 'generated/graphql';

import { CallActionPayload, MethodResponse, UIStateCallback } from '../../types';
import { ContextAPI } from '../ImpactAiContextProvider';

export const loadNonprofits = async (
  payload: CallActionPayload,
  client: ApolloClient<unknown>,
  contextApi: ContextAPI,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>,
  uiStateCallback?: UIStateCallback
): Promise<MethodResponse> => {
  const { inputPath, outputPath } = payload as {
    inputPath: string;
    outputPath: string;
    waitForCompletion?: boolean;
  };

  if (!inputPath || !outputPath) {
    throw new Error('inputPath and outputPath are required for load-nonprofits method');
  }

  // Get the input array from context
  const organizationIds = contextApi.getValue(inputPath);

  if (!organizationIds) {
    throw new Error(`No organizationIds found at inputPath "${inputPath}"`);
  }

  if (!Array.isArray(organizationIds)) {
    throw new Error(`Expected array at inputPath "${inputPath}", got ${typeof organizationIds}`);
  }

  organizationIds.map((organizationId) => {
    if (typeof organizationId !== 'string') {
      throw new Error(
        `Expected an array of strings at inputPath "${inputPath}", got ${typeof organizationId}`
      );
    }
    return organizationId;
  });

  if (organizationIds.length === 0) {
    contextApi.setValue(outputPath, []);
    if (uiStateCallback) {
      uiStateCallback('complete');
    }
    return {};
  }

  if (uiStateCallback) {
    uiStateCallback('processing');
  }

  const failedLoads: Array<{
    organizationId: string;
    error: Error;
  }> = [];

  const loadNonprofit = async (organizationId: string) => {
    try {
      const { data } = await client.query<ImpactAiNonprofitQuery, ImpactAiNonprofitQueryVariables>({
        query: ImpactAiNonprofitDocument,
        variables: {
          organizationId,
        },
        fetchPolicy: 'network-only',
      });

      const nonprofit = data?.nonprofit;

      if (!nonprofit) {
        // When nonprofit is not found, log error but count as success
        // eslint-disable-next-line no-console
        console.error(`No nonprofit found for organizationId "${organizationId}"`);
        return { success: true, organizationId, nonprofit: null };
      }

      return { success: true, organizationId, nonprofit };
    } catch (error) {
      return { success: false, organizationId, error: error as Error };
    }
  };

  // Process all nonprofits in parallel
  const nonprofitPromises = organizationIds.map((organizationId) =>
    loadNonprofit(organizationId as string)
  );
  const results = await Promise.all(nonprofitPromises);

  // Separate successful results from failures
  const successfulResults = results.filter((result) => result.success);
  const failedResults = results.filter((result) => !result.success);

  // Collect failed loads
  failedResults.forEach((result) => {
    if (!result.success && result.error) {
      failedLoads.push({
        organizationId: result.organizationId,
        error: result.error,
      });
    }
  });

  // Extract nonprofits from successful results, filtering out null values
  const nonprofits = successfulResults
    .map((result) => {
      if (result.success) {
        return result.nonprofit;
      }
      return null;
    })
    .filter(Boolean);

  // Always set the successful results in context
  contextApi.setValue(outputPath, nonprofits);

  // If there are failures, create retry callback and signal error
  if (failedLoads.length > 0) {
    const handleRetry = async () => {
      try {
        // Retry failed loads
        const retryPromises = failedLoads.map(({ organizationId }) =>
          loadNonprofit(organizationId)
        );
        const retryResults = await Promise.all(retryPromises);

        // Get successful retries
        const successfulRetries = retryResults.filter((result) => result.success);
        const stillFailedRetries = retryResults.filter((result) => !result.success);

        // Update context with successful retries by merging with existing results
        if (successfulRetries.length > 0) {
          const existingNonprofits = contextApi.getValue(outputPath) || [];
          if (!Array.isArray(existingNonprofits)) {
            throw new Error(
              `Expected array at outputPath "${outputPath}", got ${typeof existingNonprofits}. This should never happen.`
            );
          }
          const newNonprofits = successfulRetries
            .map((result) => {
              if (result.success) {
                return result.nonprofit;
              }
              return null;
            })
            .filter(Boolean);

          const allNonprofits = [...existingNonprofits, ...newNonprofits];
          contextApi.setValue(outputPath, allNonprofits);
        }

        // Check if any retries still failed
        const stillFailed = stillFailedRetries.length > 0;

        if (stillFailed) {
          // Update failed loads with new failures
          const originalFailedLoads = [...failedLoads];
          failedLoads.length = 0; // Clear the array
          stillFailedRetries.forEach((result, index) => {
            if (!result.success && result.error) {
              failedLoads.push({
                organizationId: originalFailedLoads[index].organizationId,
                error: result.error,
              });
            }
          });

          // Signal error again with the same retry callback
          if (uiStateCallback) {
            uiStateCallback('error', handleRetry);
          }
        } else if (uiStateCallback) {
          // All retries succeeded, signal completion
          uiStateCallback('complete');
        }
      } catch (retryError) {
        if (uiStateCallback) {
          uiStateCallback('error', handleRetry);
        }
      }
    };

    if (uiStateCallback) {
      uiStateCallback('error', handleRetry);
    }
    return { error: true };
  }

  // Signal completion if no failures
  if (uiStateCallback) {
    uiStateCallback('complete');
  }

  return {};
};
